import * as React from "react";

import "./template-filters.scss";

import {faAngleDown, faChevronDown, faChevronUp} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}            from "@fortawesome/react-fontawesome";
import {cx} from "emotion";

import {toGroupsByKey, toggleE} from "@Common/utils/collections";

import {
    BasicFilter,
    BasicFilterConfig,
    DiscreteRangeFilter,
    DiscreteRangeFilterConfig,
    Filter,
    FilterConfig,
    TemplatePair,
} from "../use-search-filters";
import {BasicFilterOptionAppearanceType} from "../use-search-filters";
import {BasicFilterOption} from "../use-search-filters";

export const TemplateFilters = ({
    clearFilters,
    getOptionCounts,
    filters,
    filterConfigs,
    resultSet,
    setFilterConfig,
}:{
    clearFilters: () => void,
    getOptionCounts: (filterLabel: string, resultSet: TemplatePair[]) => Record<string, number>,
    filters: Filter[],
    filterConfigs: Record<string, FilterConfig>,
    resultSet: TemplatePair[],
    setFilterConfig: (label: string, cfg: FilterConfig) => void,
}) => {
    const [collapsed, setCollapsed] = React.useState<string[]>([]);
    const toggle = (label: string) => setCollapsed((c) => toggleE(label, c));

    const someFilterIsActive = filters.some(f => getActiveCount(f, filterConfigs[f.label]) > 0);

    return (
        <div className="tmplf-trst00">
            <div className="header">
                <div className="bold-header">Filters</div>
                {someFilterIsActive && <button className="clear-all" onClick={clearFilters}>Clear All</button>}
            </div>

            {filters.map((filter) => {
                const open      = !collapsed.includes(filter.label);
                const config    = filterConfigs[filter.label];
                const activeCount  = getActiveCount(filter, config);
                const optionCounts = getOptionCounts(filter.label, resultSet);

                return (
                    <div key={filter.label} className="filter">
                        <div className="header" onClick={() => toggle(filter.label)}>
                            <div className="bold-text">{filter.label}</div>
                            {activeCount > 0 && <div className="active-badge">{activeCount}</div>}
                            <div className="expand grey-text">
                                <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
                            </div>
                        </div>

                        {open && filter.type === "basic" && (
                            <BasicFilterComponent
                                filter={filter}
                                config={config?.type === "basic" ? config : undefined}
                                optionCounts={optionCounts}
                                setFilterConfig={setFilterConfig}
                            />
                        )}

                        {open && filter.type === "discreteRange" && (
                            <DiscreteRangeFilterComponent
                                filter={filter}
                                config={config?.type === "discreteRange" ? config : undefined}
                                setFilterConfig={setFilterConfig}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const BasicFilterComponent = ({
    filter,
    config,
    optionCounts,
    setFilterConfig,
}: {
    filter: BasicFilter,
    config: BasicFilterConfig | undefined,
    optionCounts: Record<string, number>,
    setFilterConfig: (label: string, cfg: FilterConfig) => void,
}) => {
    const appearanceOptions = toGroupsByKey(
        filter.options,
        (o) => (o.typeData?.appearance?.split(":")[0] ?? "default")
    );
    return <>{Object.entries(appearanceOptions).map(([appearanceType, options], i) =>
        <div className={`options ${appearanceType}-options`} key={i}>
            {options.map((option) => {
                const active        = config?.activeOptions.includes(option.label) ?? false;
                const resultsCount  = optionCounts[option.label];
                const toggleActive  = () => {
                    const prev = config?.activeOptions ?? [];
                    const next = (
                        active
                        ? prev.filter((l) => l !== option.label)
                        : [...prev, option.label]
                    );
                    setFilterConfig(filter.label, {
                        type: "basic",
                        activeOptions: next,
                    });
                };

                let OptionComponent: BasicFilterOptionComponent;
                switch (appearanceType as BasicFilterOptionAppearanceType) {
                    case "color": OptionComponent = BasicFilterColorOptionComponent; break;
                    case "layout": OptionComponent = BasicFilterLayoutOptionComponent; break;
                    default: OptionComponent = BasicFilterDefaultOptionComponent; break;
                }

                return <OptionComponent key={option.label} {...{active, resultsCount, option, toggleActive}}/>;
            })}
        </div>
    )}</>;
};

type BasicFilterOptionComponent = React.FC<{
    active: boolean,
    resultsCount: number,
    option: BasicFilterOption,
    toggleActive: () => void,
}>;

const BasicFilterColorOptionComponent: BasicFilterOptionComponent = ({
    active,
    option,
    toggleActive,
}) =>
{
    const colorValue = ensureHexPrefix(option.typeData.appearance.split(":")[1]);
    return (
        <button className={cx("color-option", {active})} onClick={toggleActive}>
            <div className="color-preview" style={{backgroundColor: colorValue}}/>
        </button>
    );
};

function ensureHexPrefix(value: string) {
    if (value.length === 6 && /^[0-9a-fA-F]{6}$/.test(value)) {
        return '#' + value;
    }
    return value;
}


const BasicFilterDefaultOptionComponent: BasicFilterOptionComponent = ({
    active,
    resultsCount,
    option,
    toggleActive,
}) =>
{
    return (
        <label
            key={option.label}
            className={`option-line ${resultsCount == 0 ? "unavailable" : ""}`}
        >
            <input
                type="checkbox"
                checked={active}
                disabled={!active && resultsCount == 0}
                onChange={toggleActive}
            />
            <span className="option-label">{option.label}</span>
            <span className="option-count">{resultsCount}</span>
        </label>
    );
};

const BasicFilterLayoutOptionComponent: BasicFilterOptionComponent = ({active, option, toggleActive}) =>
{
    const layoutValue = option.typeData.appearance.split(":")[1];
    return (
        <div className={cx("layout-option", layoutValue, {active})} onClick={toggleActive}>
            <div className="visual"/>
            <span className="label">{option.label}</span>
        </div>
    );
}

const DiscreteRangeFilterComponent = ({
    filter,
    config,
    setFilterConfig,
}: {
    filter: DiscreteRangeFilter,
    config: DiscreteRangeFilterConfig | undefined,
    setFilterConfig: (label: string, cfg: FilterConfig) => void,
}) => {
    const {indexMin, indexMax} = config ?? {indexMin: 0, indexMax: filter.options.length - 1};

    return (
        <div className="options range-options">
            <div className="range-inputs">
                <div className="field">
                    <span className="label">Minimum</span>
                    <div className="dropdown">
                        <select
                            onChange={(e) =>
                                setFilterConfig(filter.label, {
                                    type: "discreteRange",
                                    indexMin: Number(e.target.value),
                                    indexMax: indexMax,
                                })
                            }
                            value={indexMin}
                        >
                            {filter.options.map((op, i) => (
                                <option
                                    key={i}
                                    value={i}
                                    disabled={i > indexMax}
                                >{op.label}</option>
                            ))}
                        </select>
                        <div className="caret"><FontAwesomeIcon icon={faAngleDown}/></div>
                    </div>
                </div>

                <div className="field">
                    <span className="label">Maximum</span>
                    <div className="dropdown">
                        <select
                            onChange={(e) =>
                                setFilterConfig(filter.label, {
                                    type: "discreteRange",
                                    indexMin: indexMin,
                                    indexMax: Number(e.target.value),
                                })
                            }
                            value={indexMax}
                        >
                            {filter.options.map((op, i) => (
                                <option
                                    key={i}
                                    value={i}
                                    disabled={i < indexMin}
                                >{op.label}</option>
                            ))}
                        </select>
                        <div className="caret"><FontAwesomeIcon icon={faAngleDown}/></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const getActiveCount = (filter: Filter, config: FilterConfig): number =>
{
    if (!config) {
        return 0;
    } else if (filter.type === "basic" && config.type === "basic") {
        return config.activeOptions.length;
    } else if (filter.type === "discreteRange" && config.type === "discreteRange") {
        const maxCount = config?.indexMax < (filter.options.length - 1) ? 1 : 0;
        const minCount = config?.indexMin > 0 ? 1 : 0;
        return maxCount + minCount;
    } else {
        console.warn(`Unexpected filter ('${filter.type}') and config ('${config.type}')`);
        return 0;
    }
};

